import * as StompJs from "stompjs-websocket";
import SockJS from "sockjs-client";
import store from "@/store";

export class StompClient {
  client: StompJs.Client;

  private static readonly BASE_RECONNECT_DELAY = 5000;
  private static readonly STEP1_RECONNECT_DELAY = 60000;
  private static readonly STEP2_RECONNECT_DELAY = 300000;
  private static readonly FAIL_THRESHOLD = 20;

  private failCount = 0;
  private delayStep = 0;
  private connectedListeners = new Set<() => void>();

  constructor() {
    this.client = StompJs.over(() => new SockJS(this.buildSockJsUrl()));
    this.client.reconnect_delay = StompClient.BASE_RECONNECT_DELAY;
    this.client.debug = this.onDebug;
    // 再接続間隔のチューニングを初期化時に適用
    this.setupReconnectTuning();
  }

  private buildSockJsUrl() {
    const baseUrl = process.env.VUE_APP_WS_URL || "";
    const token = store.getters["Authentication/token"];
    if (!token) {
      return baseUrl;
    }
    const separator = baseUrl.indexOf("?") === -1 ? "?" : "&";
    return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
  }
  
  connect(connectCallback: any): boolean {
    if (process.env.NODE_ENV != "jsonmode") {
      const token = store.getters["Authentication/token"];
      if (!token) {
        return false;
      }
      // 接続成功時にリセットを挟むためラップ
      const onConnect = () => {
        this.resetReconnectDelay();
        this.connectedListeners.forEach(listener => {
          try { listener(); } catch (err) { console.error("Socket subscription restore failed", err); }
        });
        if (connectCallback) { connectCallback(); }
      };
      this.client.connect(
        { token: token },
        onConnect,
        this.handleError,
        this.handleClose
      );
      return true;
    }
    return false;
  }

  // 初回・再接続の両方で発火する。返却関数で監視を解除する。
  onConnected(listener: () => void): () => void {
    this.connectedListeners.add(listener);
    return () => { this.connectedListeners.delete(listener); };
  }

  onDebug(msg: string) {
    if (process.env.NODE_ENV != "production") {
      console.log(msg);
    }
  }

  onError(e: any) {}

  onClose(e: any) {
    console.log("close", e);
  }

  Send(destination: string, body: any) {
    if (this.client.connected) {
      this.client.send(destination, {}, JSON.stringify(body));
      return true;
    }
    return false;
  }

  isConnected() {
    return this.client.connected;
  }

  disconnect() {
    this.client.disconnect();
  }

  /**
   * 接続失敗回数に応じて再接続間隔を引き延ばす
   * 20回超(1分): 60秒, 40回超(11分): 300秒, 成功で 5秒に戻す
   */
  private setupReconnectTuning() {
    // 既に設定済みなら何もしない
    if ((this as any).__reconnectTuned) {
      return;
    }
    (this as any).__reconnectTuned = true;
  }

  private bumpReconnectDelay() {
    this.failCount += 1;
    const step = Math.floor(this.failCount / StompClient.FAIL_THRESHOLD);
    if (step > this.delayStep) {
      if (step === 1) {
        this.client.reconnect_delay = StompClient.STEP1_RECONNECT_DELAY;
        this.delayStep = 1;
      } else if (step === 2) {
        this.client.reconnect_delay = StompClient.STEP2_RECONNECT_DELAY;
        this.delayStep = 2;
      }
    }
  }

  private resetReconnectDelay() {
    this.failCount = 0;
    this.delayStep = 0;
    this.client.reconnect_delay = StompClient.BASE_RECONNECT_DELAY;
  }

  private handleError = (e: any) => {
    this.bumpReconnectDelay();
    this.onError(e);
  }

  private handleClose = (e: any) => {
    this.bumpReconnectDelay();
    this.onClose(e);
  }
  
}
